// Searchable lead/customer picker, rendered as a Popover+Command combobox.
//
// Mirrors the "Khách hàng" block of the design comp at
// design/src/components/desktop/Appointments.jsx (lines 111-121). Selected
// row renders a 30×30 initials avatar + name + monospace meta line
// (`phone · leadStatusLabel`). Filterable by customer name or phone.

import { useMemo, useState } from "react";
import { ChevronDown, User2 } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { LEAD_STATUS_LABELS } from "@/features/lead/constants";
import { avatarInitials } from "@/features/dashboard/utils/avatar-initials";
import type { Lead } from "@/features/lead/types";

/** Props for {@link LeadPickerField}. */
export interface LeadPickerFieldProps {
  /** Currently-selected leadId; `null` means "not picked". */
  value: number | null;
  /** Full list of pickable leads; filtered client-side. */
  leads: readonly Lead[];
  /** Mirrors the source `useLeads` `isPending` flag. */
  isPending: boolean;
  /** Selection callback. */
  onChange: (id: number) => void;
  disabled?: boolean | undefined;
}

/**
 * Render a searchable lead picker tile. The selected-state row uses the
 * same compact layout as the design comp's "Khách hàng" block.
 */
export function LeadPickerField({
  value,
  leads,
  isPending,
  onChange,
  disabled,
}: LeadPickerFieldProps) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => (value !== null ? leads.find((l) => l.id === value) ?? null : null),
    [value, leads],
  );

  return (
    // modal: scrollable inside the appointment Dialog — see property-picker-field.
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled || isPending}
          aria-label="Chọn khách hàng"
          className={cn(
            "flex w-full items-center gap-2.5 rounded-[9px] border border-border bg-background px-2.5 py-2 text-left transition-colors",
            "hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring",
            "disabled:cursor-not-allowed disabled:opacity-60",
          )}
        >
          <LeadAvatar lead={selected} />
          <div className="flex min-w-0 flex-1 flex-col">
            {selected !== null ? (
              <>
                <span className="truncate text-[14px] font-semibold leading-tight">
                  {selected.customer.fullName}
                </span>
                <span className="truncate font-mono text-[11px] text-muted-foreground">
                  {formatLeadMeta(selected)}
                </span>
              </>
            ) : (
              <span className="text-[14px] font-medium text-muted-foreground">
                {isPending ? "Đang tải..." : "Chọn khách hàng"}
              </span>
            )}
          </div>
          <ChevronDown
            aria-hidden="true"
            className="size-3.5 shrink-0 text-muted-foreground"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] min-w-[320px] p-0"
      >
        <Command
          filter={(itemValue, search) => {
            return itemValue.includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Tìm theo tên hoặc số điện thoại..." />
          <CommandList>
            <CommandEmpty>Không có khách hàng phù hợp.</CommandEmpty>
            <CommandGroup>
              {leads.map((lead) => {
                const haystack = [
                  lead.customer.fullName,
                  lead.customer.phoneNumber,
                ]
                  .filter((s): s is string => Boolean(s))
                  .join(" ")
                  .toLowerCase();
                return (
                  <CommandItem
                    key={lead.id}
                    value={haystack || String(lead.id)}
                    onSelect={() => {
                      onChange(lead.id);
                      setOpen(false);
                    }}
                  >
                    <div className="flex w-full items-center gap-2.5">
                      <LeadAvatar lead={lead} />
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-[14px] font-semibold leading-tight">
                          {lead.customer.fullName}
                        </span>
                        <span className="truncate font-mono text-[11px] text-muted-foreground">
                          {formatLeadMeta(lead)}
                        </span>
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Subviews
// ---------------------------------------------------------------------------

function LeadAvatar({ lead }: { lead: Lead | null }) {
  if (lead === null) {
    return (
      <div className="flex size-[30px] shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <User2 aria-hidden="true" className="size-4" />
      </div>
    );
  }
  return (
    <div className="flex size-[30px] shrink-0 items-center justify-center rounded-lg bg-blue-200 text-[11px] font-semibold text-foreground">
      {avatarInitials(lead.customer.fullName)}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatLeadMeta(lead: Lead): string {
  const parts: string[] = [];
  if (lead.customer.phoneNumber) parts.push(lead.customer.phoneNumber);
  parts.push(LEAD_STATUS_LABELS[lead.status]);
  return parts.join(" · ");
}
